// MST1020_R01 BOM 관련 유틸리티 함수 및 상수

import type { AgGridReact } from "ag-grid-react";
import type { CellEditingStoppedEvent } from "ag-grid-community";
import type { RefObject } from "react";
import { gridCalendarHeight } from "src/components/common/common";
import { PRD2010_R01Dto } from "./Dto_PRD2010_R01";
import type { Store_PRD2010_R01_GridCalendarState } from "./Store_PRD2010_R01";
import { formatDate } from "src/utils/string/stringUtil";

type SetPRD2010_R01GridCalendarState =
	Store_PRD2010_R01_GridCalendarState["setPRD2010_R01GridCalendarState"];


export const headerNames_PRD2010_R01: Record<string, string> = {
	rowIndex: "",
	productOrderNo: '작업지시번호',
	compCd: '사업장',
	orderDt: '작업일자',
	orderType: '지시구분',
	workplaceCd: '작업공정',
	workProcess: '공정유형',
	jobTeam: '작업팀',
	orderEmp: '작업자',
	orderEmpNm: '작업자(명)',
	itemCd: '품목코드',
	itemNm: '품명',
	itemSpec: '규격',
	itemUnit: '단위',
	orderQty: '수량',
	remark: '비고',
}

// 원본 데이터 복구 함수
export function restoreOriginalValues(
  valueIndex: number,
  originalData: PRD2010_R01Dto,
  updatePRD2010_R01List: (index: number, data: PRD2010_R01Dto) => void,
  gridRef: any
) {
  setTimeout(() => {
    updatePRD2010_R01List(valueIndex, {
      ...originalData,
      isModified: originalData.inserted === true ? false : true
    });
    if (gridRef.current?.api) {
      const allRowNodes: any[] = [];
      gridRef.current.api.forEachNode((node: any) => {
        if (node.data && node.data.valueIndex === valueIndex) {
          allRowNodes.push(node);
        }
      });
      if (allRowNodes.length > 0) {
        const targetNode = allRowNodes[0];
        targetNode.setDataValue('itemCd', originalData.itemCd);
        targetNode.setDataValue('itemNm', originalData.itemNm);
        targetNode.setDataValue('itemSpec', originalData.itemSpec);
        targetNode.setDataValue('itemUnit', originalData.itemUnit);
      }
	}
  }, 100);
}

/** compCd 편집 종료 시(값 변경 여부 무관) orderDt 달력 트리거 */
export function handleEditingStopped(
	e: CellEditingStoppedEvent<PRD2010_R01Dto>,
	setPRD2010_R01GridCalendarState: SetPRD2010_R01GridCalendarState,
): void {
	if (e.colDef.field !== "compCd" || e.node?.rowPinned) return;
	const valueIndex = e.data?.valueIndex;
	if (valueIndex == null) return;
	setPRD2010_R01GridCalendarState({ gridCalendarTrigger: valueIndex });
}

/** gridCalendarTrigger(valueIndex)에 맞춰 그리드 DOM 기준 orderDt 달력 위치를 잡아 연다 */
export function handleGridCalendar(
	pending: number,
	gridRef: RefObject<AgGridReact<PRD2010_R01Dto> | null>,
	gridWrapperRef: RefObject<HTMLDivElement | null>,
	PRD2010_R01List: PRD2010_R01Dto[],
	setPRD2010_R01GridCalendarState: SetPRD2010_R01GridCalendarState,
): void {
	const api = gridRef.current?.api;
	const gridDiv = gridWrapperRef.current;
	if (!api || !gridDiv) {
		setPRD2010_R01GridCalendarState({ gridCalendarTrigger: null });
		return;
	}
	const rowNode = api.getRowNode(String(pending));
	if (rowNode == null || rowNode.rowIndex == null) {
		setPRD2010_R01GridCalendarState({ gridCalendarTrigger: null });
		return;
	}
	const rowIdx = rowNode.rowIndex;
	const cellSelector = `[row-index="${rowIdx}"] [col-id="orderDt"]`;
	const cellElement = gridDiv.querySelector<HTMLElement>(cellSelector);
	const foundData = PRD2010_R01List.find((r) => r.valueIndex === pending);
	if (!cellElement || !foundData) {
		setPRD2010_R01GridCalendarState({ gridCalendarTrigger: null });
		return;
	}
	const cellRect = cellElement.getBoundingClientRect();
	const gridCalendarHeightNumber = Number(gridCalendarHeight.replace("px", ""));
	const isOverflowing = cellRect.bottom + gridCalendarHeightNumber > window.innerHeight;
	const topPosition = isOverflowing
		? cellRect.top + window.scrollY - gridCalendarHeightNumber
		: cellRect.bottom + window.scrollY;

	setPRD2010_R01GridCalendarState({
		pickerPosition: { top: topPosition, left: cellRect.left + window.scrollX },
		targetRowIndex: rowIdx,
		targetField: "orderDt",
		targetDate: foundData.orderDt ? new Date(foundData.orderDt) : new Date(),
		pickerVisible: true,
		gridCalendarTrigger: null,
	});
}

/** GridCalendar 확인 후 orderType 셀로 포커스·편집 시작 */
export function afterCalendarConfirmed(
	rowIdx: number,
	field: string,
	gridRef: RefObject<AgGridReact<PRD2010_R01Dto> | null>,
): void {
	if (field !== "orderDt") return;
	const api = gridRef.current?.api;
	if (!api) return;
	if (rowIdx < 0 || rowIdx >= api.getDisplayedRowCount()) return;
	api.setFocusedCell(rowIdx, "orderType");
	api.startEditingCell({ rowIndex: rowIdx, colKey: "orderType" });
}

/** GridCalendar 날짜 변경 시 상세행 데이터 업데이트 */
export function handleGridCalendarChange(
	date: Date | undefined,
	rowIndex: number | null,
	targetField: string | null,
	PRD2010_R01List: PRD2010_R01Dto[],
	DtModifiedFunction: (date: Date, valueIndex: number) => boolean,
	updatePRD2010_R01List: (valueIndex: number, data: PRD2010_R01Dto) => void,
): void {
	if (rowIndex == null || !targetField) return;

	const foundData = PRD2010_R01List[rowIndex];
	if (!foundData) return;

	let nextIsModified = !!foundData.isModified;
	if (targetField === "orderDt") {
		const isOrderDtModified = DtModifiedFunction(date ?? new Date(), foundData.valueIndex ?? 0);
		nextIsModified = nextIsModified || isOrderDtModified;
	}

	updatePRD2010_R01List(foundData.valueIndex!, {
		...foundData,
		[targetField]: formatDate(date ?? new Date()),
		isModified: nextIsModified,
	});
}
